import React, { useState, useEffect, useRef } from "react";

import { useNavigate } from "react-router-dom";
import { signIn, confirmSignIn } from "aws-amplify/auth";

import { useAuth } from "../context/AuthProvider";
import {
  REST_API_URL,
  KAKAO,
  NAVER,
  LOG_IN_COOKIE,
  GUEST_ORDER_URL_PATH,
} from "../constants";
import API from "../service/API";
import { deleteCookie, getCookie, setCookie } from "../helpers/cookie";
import { LoadingElement } from "../helpers/utils";
import { Marketing, Privacy, TermsOfUse } from "../helpers/policies";
import { useLang } from "../context/LangProvider";
import LanguageDropdown from "../helpers/LanguageDropdown";
import "./signin/SigninAuth.css";
import { ko } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import logoImage from "../img/granhand_logo.png";
import naverIcon from "../img/social/signin_naver.png";
import kakaoIcon from "../img/social/signin_kakao.png";

registerLocale("ko", ko);

const BENEFITS = [
  {
    key: "coupon",
    label: "신규 가입 축하 쿠폰 10,000원",
  },
  {
    key: "coin",
    label: "출석 체크만 해도 적립 포인트를 드려요",
  },
  {
    key: "heart",
    label: "매일 만나는 행운! 최대 5,000원 포인트",
  },
  {
    key: "ship",
    label: "하나만 구매해도 전 제품 무료 배송",
  },
];

const terms = [
  {
    key: "tos",
    title: "서비스 이용 약관 동의",
    required: true,
    body: <TermsOfUse />,
  },
  {
    key: "privacy",
    title: "개인정보 수집 및 이용 동의",
    required: true,
    body: <Privacy />,
  },
  {
    key: "marketing",
    title: "개인정보 수집 및 이용 및 이벤트/마케팅 수신 동의",
    required: false,
    body: <Marketing />,
  },
];

const terms_nobody = [
  {
    key: "age",
    title: "만 14세 이상입니다.",
    required: true,
    body: "", // 본문 없음(체크만)
    noBody: true,
  },
];

function SigninAuthView({
  i18n,
  email,
  password,
  keep,
  errorMessage,
  setEmail,
  setPassword,
  setKeep,
  handleBack,
  handleKeyDown,
  doSignIn,
  handleSignupClick,
  handleGuestOrderTrack,
  handleFindClick,
  loginWIthNaver,
  loginWithKakao,
}) {
  return (
    <main className="signin-auth container">
      <div className="signin-auth__frame">
        <header className="signin-auth__header">
          <div className="signin-auth__toolbar">
            <button
              className="signin-auth__back"
              onClick={handleBack}
              aria-label={i18n("signin.nav.back.aria")}
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{i18n("signin.nav.back")}</span>
            </button>
            <div className="signin-auth__lang">
              <LanguageDropdown />
            </div>
          </div>

          <div className="signin-auth__brand">
            <img alt="GRANHAND." src={logoImage} />
          </div>
        </header>

        <section className="signin-auth__content">
          <form className="signin-auth__form">
            <div className="signin-auth__field">
              <label className="signin-auth__label" htmlFor="email">
                {i18n("signin.form.id.label")}
              </label>
              <input
                id="email"
                type="email"
                className="signin-auth__input"
                placeholder={i18n("signin.form.email.placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            <div className="signin-auth__field">
              <label className="signin-auth__label" htmlFor="password">
                {i18n("signin.form.password.label")}
              </label>
              <input
                id="password"
                type="password"
                className="signin-auth__input"
                onKeyDown={handleKeyDown}
                placeholder={i18n("signin.form.password.placeholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              {errorMessage ? (
                <div className="signin-auth__error" role="alert" aria-live="polite">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="5.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M8 5.1v3.3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <circle cx="8" cy="10.95" r="0.8" fill="currentColor" />
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              ) : null}

              <label className="signin-auth__keep">
                <input
                  type="checkbox"
                  checked={keep}
                  onChange={(e) => setKeep(e.target.checked)}
                />
                <span>{i18n("signin.form.keepLogin")}</span>
              </label>
            </div>

            <div className="signin-auth__actions">
              <button
                type="button"
                onClick={doSignIn}
                disabled={!email || !password}
                aria-disabled={!email || !password}
                className="signin-auth__button signin-auth__button--primary"
              >
                {i18n("signin.cta.login")}
              </button>

              <button
                type="button"
                className="signin-auth__button signin-auth__button--secondary"
                onClick={handleSignupClick}
              >
                {i18n("signin.cta.signup")}
              </button>

              <button
                type="button"
                className="signin-auth__button signin-auth__button--secondary"
                onClick={handleGuestOrderTrack}
              >
                {i18n("signin.cta.anonymous")}
              </button>

              <div className="signin-auth__links">
                <button
                  type="button"
                  className="signin-auth__link"
                  onClick={() => handleFindClick("id")}
                >
                  {i18n("signin.find.id")}
                </button>
                <button
                  type="button"
                  className="signin-auth__link"
                  onClick={() => handleFindClick("password")}
                >
                  {i18n("signin.find.password")}
                </button>
              </div>
            </div>
          </form>

          <div className="signin-auth__social">
            <button
              type="button"
              onClick={loginWIthNaver}
              className="signin-auth__social-button"
              aria-label={i18n("signin.social.naver.aria")}
            >
              <img alt="naver" src={naverIcon} />
            </button>
            <button
              type="button"
              onClick={loginWithKakao}
              className="signin-auth__social-button"
              aria-label={i18n("signin.social.kakao.aria")}
            >
              <img alt="kakao" src={kakaoIcon} />
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}

const SocialSignup = React.memo(
  ({
    onNext,
    allOk,
    setAll,
    requiredOk,
    checked,
    toggle,
    birth,
    setBirth,
    onSignup,
    onSignin,
    step,
    i18n,
  }) => {
    switch (step) {
      case 1:
        return (
          <section className="signup-auth__step">
            <div className="signup-auth__group">
                {terms.map((t) => (
                <div className="signup-auth__option" key={t.key}>
                  <label className="signup-auth__checkbox">
                      <input
                        type="checkbox"
                        checked={checked[t.key]}
                        onChange={() => toggle(t.key)}
                      />
                    <span>{i18n(`signup.terms.${t.key}.title`)}</span>
                    </label>
                  <div className="signup-auth__panel">{t.body}</div>
                </div>
                ))}
            </div>

            <div className="signup-auth__group signup-auth__group--compact">
              {terms_nobody.map((t) => (
                <label className="signup-auth__checkbox" key={t.key}>
                    <input
                      type="checkbox"
                      checked={checked[t.key]}
                      onChange={() => toggle(t.key)}
                    />
                  <span>{i18n(`signup.terms.${t.key}.title`)}</span>
                </label>
              ))}

              <label className="signup-auth__checkbox">
                <input
                  type="checkbox"
                  checked={allOk}
                  onChange={(e) => setAll(e.target.checked)}
                />
                <span>
                  <strong>GRANHAND</strong> {i18n("signup.terms.agreeAll")}
                </span>
              </label>
            </div>

              <button
                type="button"
                onClick={onNext}
                className="signup-auth__submit"
                disabled={!requiredOk}
              >
              동의하고 가입하기
              </button>
          </section>
        );

      case 2:
        return (
          <section className="signup-auth__step">
            <div className="signup-auth__intro">
              <p className="signup-auth__intro-title">
                아래 정보를 입력해 주세요.
              </p>
              <p className="signup-auth__intro-step">(1/1)</p>
            </div>

            <form className="signup-auth__form">
              <div className="signup-auth__field">
                <label className="signup-auth__label" htmlFor="birth">
                {i18n("signup.form.birth.label")}
                </label>
                <DatePicker
                  id="birth"
                  className="signup-auth__input"
                  selected={birth ? new Date(birth) : null}
                  locale="ko"
                  placeholderText={i18n("signup.form.birth.placeholder")}
                  onChange={(date) => {
                    if (!date) {
                      setBirth("");
                      return;
                    }
                    const yyyy = date.getFullYear();
                    const mm = String(date.getMonth() + 1).padStart(2, "0");
                    const dd = String(date.getDate()).padStart(2, "0");
                    setBirth(`${yyyy}-${mm}-${dd}`);
                  }}
                  minDate={new Date(1900, 0, 1)}
                  maxDate={new Date()}
                  openToDate={new Date(1990, 0, 1)}
                  dateFormat="yyyy-MM-dd"
                  showYearDropdown
                  showMonthDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={120}
                  onChangeRaw={(e) => e.preventDefault()}
                  onKeyDown={(e) => e.preventDefault()}
                  withPortal
                  portalId="signin-social-birth-date-portal"
                  onFocus={(e) => e.target.blur()}
                  renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    decreaseYear,
                    increaseYear,
                  }) => {
                    const year = date.getFullYear();
                    const month = date.getMonth() + 1;

                    return (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "4px 8px",
                        }}
                      >
                        <button type="button" onClick={decreaseYear}>
                          «
                        </button>
                        <button type="button" onClick={decreaseMonth}>
                          ‹
                        </button>
                        <span>
                          {year}년 {month}월
                        </span>
                        <button type="button" onClick={increaseMonth}>
                          ›
                        </button>
                        <button type="button" onClick={increaseYear}>
                          »
                        </button>
                      </div>
                    );
                  }}
                />
              </div>
              <button
                type="button"
                onClick={onSignup}
                disabled={!birth}
                aria-disabled={!birth}
                className="signup-auth__submit"
              >
                {i18n("signup.cta.submit")}
              </button>
            </form>
          </section>
        );

      case 3:
        return (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("signup.complete.title")}
              </p>
              <div className="signup-auth__benefits">
              {BENEFITS.map((b) => (
                  <div key={b.key} className="signup-auth__benefit">
                  <img
                    alt={i18n("signup.benefit.iconAlt")}
                    src={require(`../img/icon/${b.key}.png`)}
                      className="signup-auth__benefit-icon"
                  />
                    <span>{i18n(`signup.benefit.${b.key}`)}</span>
                  </div>
              ))}
              </div>
            </div>
            <button
              type="button"
              onClick={onSignin}
              className="signup-auth__outline-button"
            >
              {i18n("signup.complete.backToShop")}
            </button>
          </section>
        );

      default:
        return null;
    }
  }
);

export default function Signin() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const { i18n, cafe24RedirectUrl } = useLang();
  const [isLoading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [birth, setBirth] = useState(null);
  const [isSocialSignup, setSocialSignup] = useState(null);
  const [checked, setChecked] = useState(() =>
    Object.fromEntries(terms.map((t) => [t.key, false]))
  );
  const tempAccount = useRef();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keep, setKeep] = useState(false);

  useEffect(() => {
    if (window.Kakao && !window.Kakao.isInitialized()) {
      window.Kakao.init(KAKAO.API_KEY);
    }
    getKakaoSession();
    getNaverSession();

    window.result = async function (payload) {
      fetch(
        `${REST_API_URL}/mok/result?txId=${encodeURIComponent(
          JSON.parse(payload).txId
        )}`
      )
        .then((r) => r.json())
        .then((json) => {});
    };

    return () => {
      delete window.result;
    };
  }, []);

  const getKakaoSession = async () => {
    const cookie = getCookie(LOG_IN_COOKIE);
    if (cookie && cookie === "kakao") {
      deleteCookie(LOG_IN_COOKIE);
      setLoading(true);
      fetch(`${REST_API_URL}${KAKAO.SESSION_PATH}`, {
        method: "GET",
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.authenticated) {
            doKakaoSiginin(data.me);
          } else {
            setLoading(false);
          }
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  };

  const getNaverSession = async () => {
    const cookie = getCookie(LOG_IN_COOKIE);
    if (cookie && cookie === "naver") {
      deleteCookie(LOG_IN_COOKIE);
      setLoading(true);
      fetch(`${REST_API_URL}${NAVER.SESSION_PATH}`, {
        method: "GET",
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.authenticated) {
            doNaverSiginin(data.me.response);
          } else {
            setLoading(false);
          }
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  };

  const handleSignup = async () => {
    const account = tempAccount.current;
    await API.createUser(
      account.email,
      account.name,
      account.phone,
      null,
      null,
      account.method,
      account.dataId,
      checked.marketing
    );
    let authUser = await signIn({
      username: account.email,
      options: {
        authFlowType: "CUSTOM_WITHOUT_SRP",
      },
    });
    const answer = authUser.nextStep?.additionalInfo?.answer;
    await confirmSignIn({ challengeResponse: answer });
    setSocialSignup(3);
  };

  const handleSocialSignupComplete = async () => {
    await refresh();
  };

  const doKakaoSiginin = async (data) => {
    try {
      const account = data.kakao_account;
      const phone = account.phone_number.replace(/[\s-]/g, ""); // +82 10-1234-1234
      const u = await API.checkUser(account.email, phone, "KAKAO", data.id);
      if (!u) {
        tempAccount.current = {
          email: account.email,
          name: account.name,
          phone: phone,
          dataId: data.id,
          method: "KAKAO",
        };
        setSocialSignup(1);
      } else {
        const providerType = u.attributes?.["custom:provider"];
        if (providerType && providerType.toLowerCase() === "kakao") {
          let authUser = await signIn({
            username: u.username,
            options: {
              authFlowType: "CUSTOM_WITHOUT_SRP",
            },
          });
          const answer = authUser.nextStep?.additionalInfo?.answer;
          await confirmSignIn({ challengeResponse: answer });
          await refresh();
        } else {
          nav("/existing-account", {
            state: {
              user: u,
            },
          });
        }
      }
      setLoading(false);
    } catch (err) {
      if (err.name === "NotAuthorizedException") {
        setErrorMessage(i18n("signin.result.fail"));
      }
      setLoading(false);
    }
  };

  const doNaverSiginin = async (data) => {
    try {
      const phone = data.mobile_e164;
      const u = await API.checkUser(data.email, phone, "NAVER", data.id);
      if (!u) {
        tempAccount.current = {
          email: data.email,
          name: data.name,
          phone: phone,
          dataId: data.id,
          method: "NAVER",
        };
        setSocialSignup(1);
      } else {
        const providerType = u.attributes?.["custom:provider"];
        if (providerType && providerType.toLowerCase() === "naver") {
          let authUser = await signIn({
            username: u.username,
            options: {
              authFlowType: "CUSTOM_WITHOUT_SRP",
            },
          });
          const answer = authUser.nextStep?.additionalInfo?.answer;
          await confirmSignIn({ challengeResponse: answer });
          await refresh();
        } else {
          nav("/existing-account", {
            state: {
              user: u,
            },
          });
        }
      }
      setLoading(false);
    } catch (err) {
      if (err.name === "NotAuthorizedException") {
        setErrorMessage(i18n("signin.result.fail"));
      }
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      doSignIn();
    }
  };

  const doSignIn = async () => {
    setLoading(true);
    try {
      await signIn({ username: email, password });
      await refresh();
      setLoading(false);
    } catch (e) {
      const status = await API.getUserStatus(email);
      if (status === "FORCE_CHANGE_PASSWORD") {
        nav(`/find-account?type=password`, {
          state: { email },
        });
      } else {
        if (e.name === "NotAuthorizedException") {
          if (e.message === "Incorrect username or password.") {
            setErrorMessage(i18n("signin.result.incorrect"));
          } else {
            setErrorMessage(
              i18n("signin.result.fail") // "로그인 실패"
            );
          }
        } else if (e.name === "UserNotFoundException") {
          if (e.message === "User does not exist.") {
            setErrorMessage(i18n("signin.result.notExist"));
          } else {
            setErrorMessage(
              i18n("signin.result.fail") // "로그인 실패"
            );
          }
        } else {
          setErrorMessage(
            i18n("signin.result.fail") // "로그인 실패"
          );
        }
        setLoading(false);
      }
    }
  };

  const handleSignupClick = () => {
    nav("/signup");
  };

  const handleGuestOrderTrack = () => {
    window.location.replace(cafe24RedirectUrl + GUEST_ORDER_URL_PATH);
  };

  const loginWIthNaver = () => {
    setLoading(true);
    setCookie(LOG_IN_COOKIE, "naver");
    const redirectUri = `${REST_API_URL}${NAVER.CALLBACK_PATH}`;
    const state = crypto.randomUUID();
    const naverAuthUrl = `${NAVER.AUTH_URL}?response_type=code&client_id=${
      NAVER.CLIENT_ID
    }&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;

    window.location.href = naverAuthUrl;
  };

  const loginWithKakao = () => {
    if (!window.Kakao) return;
    setLoading(true);
    setCookie(LOG_IN_COOKIE, "kakao");
    window.Kakao.Auth.authorize({
      redirectUri: `${REST_API_URL}${KAKAO.CALLBACK_PATH}`,
    });
  };

  const handleBack = () => {
    window.location.replace(cafe24RedirectUrl);
  };

  const handleFindClick = (type) => {
    nav(`/find-account?type=${type}`);
  };

  const handleNext = () => {
    setSocialSignup(isSocialSignup + 1);
  };

  const requiredOk = terms
    .concat(terms_nobody)
    .filter((t) => t.required)
    .every((t) => checked[t.key]);
  const allOk = [...terms, ...terms_nobody].every((t) => checked[t.key]);
  const toggle = (key) =>
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));

  const setAll = (val) =>
    setChecked(
      Object.fromEntries([...terms, ...terms_nobody].map((t) => [t.key, val]))
    );

  if (isLoading) return <LoadingElement />;

  return isSocialSignup !== null ? (
    <main className="signup-auth container">
      <div className="signup-auth__frame">
        <div className="signup-auth__toolbar">
          <button
            type="button"
            className="signup-auth__back"
            onClick={handleBack}
            aria-label={i18n("signin.nav.back.aria")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M15 18l-6-6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{i18n("signin.nav.back")}</span>
          </button>
          <div className="signup-auth__lang">
            <LanguageDropdown />
          </div>
        </div>

        <div className="signup-auth__brand">
          <img alt="GRANHAND." src={logoImage} />
        </div>

        <section className="signup-auth__content">
          <SocialSignup
            onNext={handleNext}
            checked={checked}
            toggle={toggle}
            allOk={allOk}
            setAll={setAll}
            requiredOk={requiredOk}
            birth={birth}
            setBirth={setBirth}
            onSignup={handleSignup}
            onSignin={handleSocialSignupComplete}
            step={isSocialSignup}
            i18n={i18n}
          />
        </section>
      </div>
    </main>
  ) : (
    <SigninAuthView
      i18n={i18n}
      email={email}
      password={password}
      keep={keep}
      errorMessage={errorMessage}
      setEmail={setEmail}
      setPassword={setPassword}
      setKeep={setKeep}
      handleBack={handleBack}
      handleKeyDown={handleKeyDown}
      doSignIn={doSignIn}
      handleSignupClick={handleSignupClick}
      handleGuestOrderTrack={handleGuestOrderTrack}
      handleFindClick={handleFindClick}
      loginWIthNaver={loginWIthNaver}
      loginWithKakao={loginWithKakao}
    />
  );
}
